import cx from "classnames";
import React, { PropsWithChildren, useCallback, useRef } from "react";

import { useSorterConfig } from "context/SorterContext/SorterContextProvider";
import { SortDirection, SorterConfig, SorterKey } from "context/SorterContext/types";

import IcSortable from "img/ic_sortable.svg?react";
import IcSortedAsc from "img/ic_sorted_asc.svg?react";
import IcSortedDesc from "img/ic_sorted_desc.svg?react";

const directionIconMap: Record<SortDirection, React.ComponentType> = {
  asc: IcSortedAsc,
  desc: IcSortedDesc,
  unspecified: IcSortable,
};
const directionSequence: SortDirection[] = ["desc", "asc", "unspecified"];

export function Sorter(
  props: PropsWithChildren<{ direction: SortDirection; onChange: (direction: SortDirection) => void }>
) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.currentTarget.contains(e.target as Node)) {
      e.stopPropagation();
      const currentIndex = directionSequence.indexOf(props.direction);
      const nextIndex = (currentIndex + 1) % directionSequence.length;
      props.onChange(directionSequence[nextIndex]);
    }
  };

  const Icon = directionIconMap[props.direction];

  return (
    <button
      className={cx("inline-flex items-center [text-align:inherit] [text-transform:inherit]", {
        "text-primary-alpha-darker": props.direction !== "unspecified",
      })}
      onClickCapture={handleClick}
    >
      {props.children}
      <SortIcon direction={props.direction} />
    </button>
  );
}

export function useSorterHandlers<SortField extends string | "unspecified">(
  key: SorterKey,
  initialConfig?: SorterConfig<SortField>
) {
  const [config, setConfig] = useSorterConfig<SortField | "unspecified">(key, initialConfig);
  const onChangeCache = useRef<Partial<Record<SortField | "unspecified", (direction: SortDirection) => void>>>({});

  const getSorterProps = useCallback(
    (field: SortField | "unspecified") => {
      let cachedHandler = onChangeCache.current[field];

      if (!cachedHandler) {
        cachedHandler = (newDirection: SortDirection) => {
          setConfig({
            orderBy: field,
            direction: newDirection,
          });
        };
        onChangeCache.current[field] = cachedHandler;
      }

      return {
        direction: config.orderBy === field ? config.direction : "unspecified",
        onChange: cachedHandler!,
      };
    },
    [config.direction, config.orderBy, setConfig]
  );

  const setOrderBy = useCallback(
    (field: SortField | "unspecified") => {
      setConfig((prev) => ({
        ...prev,
        orderBy: field,
      }));
    },
    [setConfig]
  );

  const setDirection = useCallback(
    (direction: SortDirection) => {
      setConfig((prev) => ({
        ...prev,
        direction,
      }));
    },
    [setConfig]
  );

  return { getSorterProps, orderBy: config.orderBy, direction: config.direction, setOrderBy, setDirection };
}

const SortIcon = ({ direction }: { direction: SortDirection }) => {
  return (
    <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7.37207 1.5876C7.65115 1.10422 8.34885 1.10422 8.62793 1.5876L10.8362 5.4124C11.1153 5.89578 10.7664 6.5 10.2082 6.5H5.79175C5.23359 6.5 4.88475 5.89578 5.16383 5.4124L7.37207 1.5876Z"
        fill={direction === "asc" ? "#00BD79" : "#6D6D6D"}
      />
      <path
        d="M8.62793 15.4124C8.34885 15.8958 7.65115 15.8958 7.37207 15.4124L5.16382 11.5876C4.88475 11.1042 5.23359 10.5 5.79175 10.5L10.2082 10.5C10.7664 10.5 11.1153 11.1042 10.8362 11.5876L8.62793 15.4124Z"
        fill={direction === "desc" ? "#00BD79" : "#6D6D6D"}
      />
    </svg>
  );
};
